if (!customElements.get('vehicle-finder')) {
  class VehicleFinder extends HTMLElement {
    connectedCallback() {
      this.optionsPath = this.dataset.optionsPath || '/apps/vrm/options';
      this.optionsPending = 0;
      this.loader = VehicleFinder.ensureLoader();
      this.tabs = this.querySelectorAll('.vehicle-finder__tab');
      this.panels = this.querySelectorAll('.vehicle-finder__panel');
      this.form = this.querySelector('form[data-panel="vehicle"]');
      this.plateForm = this.querySelector('form[data-panel="plate"]');
      this.selects = this.form
        ? Array.from(this.form.querySelectorAll('.vehicle-finder__select')).sort(
          (a, b) => Number(a.dataset.step) - Number(b.dataset.step)
        )
        : [];

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.tab));
      });

      this.selects.forEach((select) => {
        select.addEventListener('focus', () => this.handleSelectFocus(select));
        select.addEventListener('change', () => this.handleSelectChange(select));
      });

      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }
      if (this.plateForm) {
        this.plateForm.addEventListener('submit', this.handlePlateSubmit.bind(this));
      }

      // Clear is only usable once a vehicle search is active.
      const searchActive = new URLSearchParams(window.location.search).get('view') === 'vehicle';
      this.querySelectorAll('[data-vf-clear]').forEach((btn) => {
        btn.disabled = !searchActive;
        btn.addEventListener('click', this.handleClear.bind(this));
      });

      this.vrmResolvedHandler = this.handleVrmResolved.bind(this);
      document.addEventListener('on:vrm:resolved', this.vrmResolvedHandler);

      this.applyInitialValues();
    }

    disconnectedCallback() {
      document.removeEventListener('on:vrm:resolved', this.vrmResolvedHandler);
    }

    /**
     * Creates (once) the shared full-page loading overlay.
     * @returns {HTMLElement} The overlay element.
     */
    static ensureLoader() {
      let loader = document.querySelector('.vehicle-finder-loader');
      if (!loader) {
        loader = document.createElement('div');
        loader.className = 'vehicle-finder-loader';
        loader.setAttribute('role', 'status');
        loader.hidden = true;
        loader.innerHTML = '<div class="vehicle-finder-loader__spinner"></div>'
          + '<span class="visually-hidden">Loading…</span>';
        document.body.appendChild(loader);
      }
      return loader;
    }

    /**
     * Shows the overlay (reference-counted for overlapping requests).
     */
    showLoader() {
      this.optionsPending += 1;
      this.loader.hidden = false;
    }

    /**
     * Hides the overlay once all in-flight requests have settled.
     */
    hideLoader() {
      this.optionsPending = Math.max(0, this.optionsPending - 1);
      if (this.optionsPending === 0) this.loader.hidden = true;
    }

    /**
     * Activates a tab and shows its matching panel.
     * @param {string} tabName - 'vehicle' or 'plate'.
     */
    activateTab(tabName) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tab === tabName;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.panel !== tabName;
      });
    }

    /**
     * Fetches a select's options when it gains focus, if not already loaded.
     * In the normal flow, model/year are loaded eagerly by handleSelectChange
     * so this is a no-op for them; it only fetches when a pre-filled select
     * (from URL initial values) is opened to change its value.
     * @param {HTMLSelectElement} select - The focused select.
     */
    handleSelectFocus(select) {
      const params = this.optionsParams(select.dataset.field);
      const key = params.toString();
      if (select.dataset.loadedKey === key || select.dataset.loading) return;
      this.fetchOptions(select, params, key);
    }

    /**
     * Builds the proxy query params for a given field's cascade.
     * @param {string} field - 'make', 'model' or 'year'.
     * @returns {URLSearchParams} Query params.
     */
    optionsParams(field) {
      const params = new URLSearchParams();
      const make = this.fieldValue('make');
      const model = this.fieldValue('model');

      if ((field === 'model' || field === 'year') && make) {
        params.set('make', make);
      }
      if (field === 'year' && model) {
        params.set('model', model);
      }
      return params;
    }

    /**
     * Returns the current value of a field's select.
     * @param {string} field - Field name.
     * @returns {string} Selected value.
     */
    fieldValue(field) {
      const select = this.selects.find((s) => s.dataset.field === field);
      return select ? select.value : '';
    }

    /**
     * Fetches and populates options for a select from the proxy.
     * @param {HTMLSelectElement} select - Target select.
     * @param {URLSearchParams} params - Cascade params.
     * @param {string} key - Cache key (the param string).
     */
    async fetchOptions(select, params, key) {
      select.dataset.loading = 'true';
      this.showLoader();
      try {
        const query = params.toString();
        const url = query ? `${this.optionsPath}?${query}` : this.optionsPath;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`options proxy responded ${res.status}`);

        const data = await res.json();
        let options = Array.isArray(data.options) ? data.options : [];
        if (select.dataset.field === 'make' || select.dataset.field === 'model') {
          options = options.slice().sort((a, b) => String(a).trim().localeCompare(String(b).trim()));
        }
        VehicleFinder.populate(select, options);
        select.dataset.loadedKey = key;
      } catch (err) {
        console.warn('[vehicle-finder]', err);
      } finally {
        delete select.dataset.loading;
        this.hideLoader();
      }
    }

    /**
     * Replaces a select's options, keeping the placeholder and current value.
     * @param {HTMLSelectElement} select - Target select.
     * @param {string[]} options - Option values.
     */
    static populate(select, options) {
      const current = select.value;
      select.textContent = '';
      select.add(new Option(select.dataset.placeholder || '', ''));
      options.forEach((value) => select.add(new Option(value, value)));
      if (current && options.includes(current)) select.value = current;
    }

    /**
     * Resets a select back to just its placeholder.
     * @param {HTMLSelectElement} select - Target select.
     */
    static reset(select) {
      select.textContent = '';
      select.add(new Option(select.dataset.placeholder || '', ''));
      delete select.dataset.loadedKey;
    }

    /**
     * Cascades a selection change: resets downstream selects and eagerly
     * loads the next select's options as soon as this one is chosen.
     * @param {HTMLSelectElement} select - The changed select.
     */
    handleSelectChange(select) {
      const step = Number(select.dataset.step);
      const hasValue = Boolean(select.value);

      this.selects.forEach((downstream) => {
        const downstreamStep = Number(downstream.dataset.step);
        if (downstreamStep <= step) return;

        downstream.value = '';
        VehicleFinder.reset(downstream);
        downstream.disabled = downstreamStep === step + 1 ? !hasValue : true;
      });

      if (!hasValue) return;

      const next = this.selects.find((s) => Number(s.dataset.step) === step + 1);
      if (next) {
        const params = this.optionsParams(next.dataset.field);
        this.fetchOptions(next, params, params.toString());
      }
    }

    /**
     * Pre-fills the dropdowns from a vehicle object without hitting the proxy.
     * The full option list for a pre-filled select is fetched lazily, only
     * when the shopper focuses it to change the value.
     * @param {object} vehicle - { make, model, year }.
     */
    applyVehicle(vehicle) {
      let chainValue = true;

      this.selects.forEach((select, index) => {
        if (index > 0) {
          select.disabled = !chainValue;
        }
        if (!chainValue) {
          chainValue = false;
          return;
        }

        const value = vehicle[select.dataset.field];
        if (value) {
          if (![...select.options].some((opt) => opt.value === value)) {
            select.add(new Option(value, value));
          }
          select.value = value;
          delete select.dataset.loadedKey;
        } else {
          chainValue = false;
        }
      });
    }

    /**
     * Reflects make/model/year/plate from the URL in the UI on load.
     */
    applyInitialValues() {
      const params = new URLSearchParams(window.location.search);

      this.applyVehicle({
        make: params.get('make') || '',
        model: params.get('model') || '',
        year: params.get('year') || ''
      });

      const plate = params.get('plate');
      if (plate && this.plateForm) {
        const input = this.plateForm.querySelector('[data-vf-plate]');
        if (input) input.value = plate;
        this.activateTab('plate');
      }
    }

    /**
     * Applies a VRM-resolved vehicle to the YMM dropdowns.
     * @param {CustomEvent} evt - on:vrm:resolved event.
     */
    handleVrmResolved(evt) {
      if (evt.detail) this.applyVehicle(evt.detail);
    }

    /**
     * Runs a search: if the vehicle collection is already on the page it is
     * reloaded in place; otherwise the browser navigates to it.
     * @param {string} url - Destination search URL.
     */
    static navigate(url) {
      const collection = document.querySelector('vehicle-collection');
      if (collection && typeof collection.reloadFromUrl === 'function') {
        window.history.pushState({}, '', url);
        collection.reloadFromUrl();
        collection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.location.href = url;
      }
    }

    /**
     * Searches the vehicle collection for the selected make/model/year.
     * @param {Event} evt - Submit event.
     */
    handleSubmit(evt) {
      evt.preventDefault();

      const action = this.form.getAttribute('action') || '/collections/all';
      const params = new URLSearchParams();
      params.set('view', 'vehicle');

      this.selects.forEach((select) => {
        if (select.value) params.set(select.dataset.field, select.value);
      });

      VehicleFinder.navigate(`${action}?${params}`);
    }

    /**
     * Returns to the standard collection page, dropping the vehicle view.
     * @param {Event} evt - Click event.
     */
    handleClear(evt) {
      const form = evt.currentTarget.closest('form');
      const action = (form && form.getAttribute('action')) || '/collections/all';
      window.location.href = action;
    }

    /**
     * Searches the vehicle collection for a license plate.
     * @param {Event} evt - Submit event.
     */
    handlePlateSubmit(evt) {
      evt.preventDefault();

      const input = this.plateForm.querySelector('[data-vf-plate]');
      const plate = (input.value || '').replace(/\s+/g, '').toUpperCase();
      if (!plate) {
        input.focus();
        return;
      }

      const action = this.plateForm.getAttribute('action') || '/collections/all';
      const params = new URLSearchParams();
      params.set('view', 'vehicle');
      params.set('plate', plate);

      VehicleFinder.navigate(`${action}?${params}`);
    }
  }

  customElements.define('vehicle-finder', VehicleFinder);
}
